import { useEffect, useMemo, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { splitEmailBody } from '../utils/emailSplitter'

/**
 * Reads the current theme from the `.dark` class on <html>, which is the
 * source of truth for Tailwind's `dark:` variants (see styles/index.css:
 * `@variant dark (&:where(.dark, .dark *))`). MUI Joy's `useColorScheme`
 * returns `'system'` when the user hasn't explicitly picked, which doesn't
 * reflect the actual applied theme — the class does.
 */
function useIsDark(): boolean {
    const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'))
    useEffect(() => {
        const el = document.documentElement
        const update = () => setIsDark(el.classList.contains('dark'))
        update()
        const observer = new MutationObserver(update)
        observer.observe(el, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])
    return isDark
}

interface EmailIframeProps {
    html: string
    /** Broker footer start phrase; anything at-or-after this in the DOM becomes collapsible. */
    footerStartPhrase?: string | null
    /** Tailwind class names applied to the outer wrapper. */
    className?: string
    /** Bubble background color. Painted inside the iframe so it matches the surrounding bubble exactly (the iframe canvas otherwise defaults to white). */
    bgColor?: string
    /** Fired when the user clicks anywhere in the iframe body (excluding `<a>` and the `•••` toggle). Used so the surrounding chat bubble can collapse on tap even though iframe clicks don't bubble out natively. */
    onBodyClick?: () => void
}

const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'a',
        'div',
        'span',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'b',
        'i',
        'table',
        'tbody',
        'tr',
        'td',
        'th',
        'thead',
        'ul',
        'ol',
        'li',
        'hr',
        'font',
        'blockquote',
        'pre',
        'code',
        'small',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'style', 'class', 'width', 'height', 'align', 'border', 'face', 'size', 'cellpadding', 'cellspacing'],
    ALLOW_DATA_ATTR: false,
}

interface IframeTheme {
    text: string
    link: string
    muted: string
    quoteBorder: string
    toggleBg: string
    toggleHover: string
    toggleText: string
}

const LIGHT_THEME: IframeTheme = {
    text: '#363739',
    link: '#1a73e8',
    muted: '#5f6368',
    quoteBorder: '#dadce0',
    toggleBg: '#f1f3f4',
    toggleHover: '#e8eaed',
    toggleText: '#5f6368',
}

const DARK_THEME: IframeTheme = {
    text: '#e3e5e8',
    link: '#8ab4f8',
    muted: '#9aa0a6',
    quoteBorder: '#3a3a3a',
    toggleBg: '#3a3a3a',
    toggleHover: '#4a4a4a',
    toggleText: '#cfd2d6',
}

function buildIframeStyles(theme: IframeTheme, bgColor: string): string {
    // html height: auto is load-bearing — without it, <html> fills iframe height,
    // making documentElement.scrollHeight feed the iframe's own height back to itself.
    return `
    html { height: auto; background: ${bgColor}; }
    html, body {
        margin: 0;
        padding: 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 13px;
        line-height: 1.5;
        color: ${theme.text};
        background: ${bgColor};
        word-wrap: break-word;
        overflow-wrap: break-word;
    }
    body { padding: 2px 0; overflow-wrap: anywhere; }
    /* Neutralize absurd left indentation authors set for desktop mail clients.
     * Emails often ship with margin: 0in 0in 8pt 0.5in on every paragraph,
     * which in our narrow chat bubble renders as a big blank gutter. Keep the
     * vertical spacing that carries structure, just zero out horizontal indent. */
    body *:not(blockquote):not(blockquote *):not(.numeo-hidden):not(.numeo-hidden *) {
        margin-left: 0 !important;
        padding-left: 0 !important;
        text-indent: 0 !important;
    }
    /* Neutralize email-author colors & backgrounds so content reads on our theme bubble */
    body, body * { background-color: transparent !important; }
    body *:not(a):not(.numeo-toggle) { color: inherit !important; }
    /* Authors sometimes set 22pt+ inline font-size for headers that look fine
     * in their email client but dwarf everything in our narrow chat bubble.
     * Force inheritance from the 13px body so every email reads uniformly.
     * Bold/italic/weight emphasis still comes through. */
    body *:not(.numeo-toggle) { font-size: inherit !important; line-height: inherit !important; }
    img { max-width: 100%; height: auto; border-radius: 4px; }
    a { color: ${theme.link} !important; text-decoration: underline; }
    table { max-width: 100%; border-collapse: collapse; }
    /* Gmail sometimes stamps copy-pasted tables with width:0px + table-layout:fixed,
     * which collapses the whole block to zero width. Force those back to auto. */
    table[width="0"], table[style*="width:0p"], table[style*="width: 0p"], table[style*="width:0px"] { width: auto !important; }
    blockquote { margin: 8px 0; padding-left: 10px; border-left: 2px solid ${theme.quoteBorder}; color: ${theme.muted} !important; }
    p { margin: 0 0 6px 0; }
    p:last-child { margin-bottom: 0; }
    pre, code { white-space: pre-wrap; word-wrap: break-word; }
    .numeo-hidden { display: none; }
    .numeo-hidden.numeo-open { display: block; }
    .numeo-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 18px;
        padding: 0 8px;
        margin: 4px 0;
        background: ${theme.toggleBg};
        border: none;
        border-radius: 9px;
        cursor: pointer;
        color: ${theme.toggleText} !important;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 2px;
        line-height: 1;
    }
    .numeo-toggle:hover { background: ${theme.toggleHover}; }
    `
}

function buildIframeScript(frameId: string): string {
    return `
    (function () {
        var FRAME_ID = ${JSON.stringify(frameId)};
        var toggle = document.querySelector('.numeo-toggle');
        var hidden = document.querySelector('.numeo-hidden');
        function measure() {
            // Measure body via Range for accuracy — scrollHeight can round up and includes
            // margin collapse artifacts. Range gives the true rendered bottom.
            var range = document.createRange();
            range.selectNodeContents(document.body);
            var rect = range.getBoundingClientRect();
            var h = Math.ceil(rect.height);
            return Math.max(h, document.body.getBoundingClientRect().height);
        }
        function post() {
            parent.postMessage({ type: 'numeo-iframe-height', id: FRAME_ID, height: measure() }, '*');
        }
        if (toggle && hidden) {
            toggle.addEventListener('click', function () {
                hidden.classList.toggle('numeo-open');
                post();
            });
        }
        // Forward body clicks to parent so the surrounding bubble can collapse
        // on tap. Iframe documents are isolated, so parent's onClick never sees
        // these natively. Skip clicks on the toggle button and on links — those
        // have their own meaning inside the iframe.
        document.body.addEventListener('click', function (ev) {
            var t = ev.target;
            while (t && t !== document.body) {
                var tag = (t.tagName || '').toLowerCase();
                if (tag === 'a' || (t.classList && t.classList.contains('numeo-toggle'))) return;
                t = t.parentElement;
            }
            parent.postMessage({ type: 'numeo-iframe-click', id: FRAME_ID }, '*');
        });
        Array.prototype.forEach.call(document.querySelectorAll('a'), function (a) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        });
        var imgs = document.querySelectorAll('img');
        var pending = imgs.length;
        if (pending === 0) post();
        Array.prototype.forEach.call(imgs, function (img) {
            if (img.complete) { if (--pending <= 0) post(); return; }
            img.addEventListener('load', function () { if (--pending <= 0) post(); post(); });
            img.addEventListener('error', function () { if (--pending <= 0) post(); });
        });
        if (window.ResizeObserver) {
            var ro = new ResizeObserver(post);
            ro.observe(document.body);
        } else {
            window.addEventListener('resize', post);
        }
        setTimeout(post, 0);
        setTimeout(post, 120);
    })();
    `
}

function buildSrcDoc(visibleHtml: string, hiddenHtml: string, frameId: string, theme: IframeTheme, bgColor: string): string {
    const safeVisible = DOMPurify.sanitize(visibleHtml, SANITIZE_CONFIG)
    const safeHidden = hiddenHtml ? DOMPurify.sanitize(hiddenHtml, SANITIZE_CONFIG) : ''
    const toggle = safeHidden ? '<button class="numeo-toggle" type="button" aria-label="Show trimmed content">&bull;&bull;&bull;</button>' : ''
    const hiddenBlock = safeHidden ? `<div class="numeo-hidden">${safeHidden}</div>` : ''

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<base target="_blank" />
<style>${buildIframeStyles(theme, bgColor)}</style>
</head>
<body>
<div class="numeo-visible">${safeVisible}</div>
${toggle}
${hiddenBlock}
<script>${buildIframeScript(frameId)}</script>
</body>
</html>`
}

let frameIdCounter = 0

export const EmailIframe = ({ html, footerStartPhrase, className, bgColor, onBodyClick }: EmailIframeProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [height, setHeight] = useState(24)
    const frameId = useMemo(() => `email-iframe-${++frameIdCounter}`, [])

    const isDark = useIsDark()
    const theme = isDark ? DARK_THEME : LIGHT_THEME
    const resolvedBg = bgColor ?? (isDark ? '#282828' : '#ffffff')

    const srcDoc = useMemo(() => {
        const { visible, hidden } = splitEmailBody(html, footerStartPhrase ?? null)
        return buildSrcDoc(visible, hidden, frameId, theme, resolvedBg)
    }, [html, footerStartPhrase, frameId, theme, resolvedBg])

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            const data = e.data as { type?: string; id?: string; height?: number } | null
            if (!data || data.id !== frameId) return
            if (data.type === 'numeo-iframe-click') {
                onBodyClick?.()
                return
            }
            if (data.type !== 'numeo-iframe-height') return
            if (typeof data.height === 'number' && data.height > 0) {
                setHeight(Math.min(data.height + 2, 4000))
            }
        }
        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [frameId, onBodyClick])

    return (
        <iframe
            ref={iframeRef}
            data-frame-id={frameId}
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            title="Email body"
            className={className}
            style={{ width: '100%', height, border: 0, display: 'block', background: resolvedBg, colorScheme: isDark ? 'dark' : 'light' }}
        />
    )
}
